'use client';

import { ContainerLG } from '..';
import styles from './AnimatedTextSection.module.css';
import { RareLogo } from './RareLogo';
import { I_playfair } from './StyleConstants';
import { CustomImage as Image } from '..';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { Children, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import gsap from 'gsap';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useTranslations } from 'next-intl';
import { splitGraphemes } from 'utils/splitWords';

export const getSplitChars = (input: string, className: string) => {
  const op = splitGraphemes(input.toUpperCase())
    .map((letter, index) => {
      if (letter !== 'O' && letter !== ' ') {
        return (
          <span key={index} className={className}>
            {letter}
          </span>
        );
      }
      if (letter === ' ')
        return (
          <span key={index} className={className}>
            &nbsp;
          </span>
        );
      if (letter === 'O')
        return (
          <I_playfair key={index} className={className}>
            {letter}
          </I_playfair>
        );
    });

  return op;
};

export const AnimatedTextSection = () => {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

    const gsapContext = useGsapContext();
    const t = useTranslations('ring');
    const { width } = useWindowSize();
    const root = useRef<HTMLDivElement>(null);

    const LETTER_CLASSNAME = 'animated-text-letters';

    const paraImage = (file: string) => (
      <Image
        loading="lazy"
        src={`web_v2/rare/para-imgs/${file}`}
        alt=""
        width="1057"
        height="375"
      />
    );

    const animationInit = () => {
      gsapContext.add(() => {
        if (!root.current) return;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: root.current,
            start: width < 767 ? 'top 90%' : 'top 60%',
            // end: width < 767 ? 'top 30%' : 'top 20%',
            end: 'center 40%',
            scrub: true,
          },
        });

        tl.fromTo(
          root.current.querySelectorAll(`.${LETTER_CLASSNAME}`),
          { opacity: 0, yPercent: 100 },
          { opacity: 1, yPercent: 0, stagger: 0.04 },
        );

        tl.fromTo(
          root.current.querySelectorAll('img'),
          { clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)' },
          {
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
            stagger: 0.7,
          },
          '<',
        );

        // end -------
      });
    };

    useIsomorphicLayoutEffect(() => {
      intersectionObserver.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].intersectionRatio <= 0) return;
          animationInit();
          intersectionObserver.current?.disconnect();
        },
        {
          root: null,
          rootMargin: '300px',
          threshold: 0.01,
        },
      );

      intersectionObserver.current.observe(root.current as Element);
    }, []);

    return (
      <section className={styles.root}>
        <ContainerLG ref={root}>
          <p>
            {t.rich('ring.rare.animatedText.sentence', {
              line: (chunks) => (
                <span className="sentence">
                  {Children.map(chunks, (chunk) =>
                    typeof chunk === 'string'
                      ? getSplitChars(chunk, LETTER_CLASSNAME)
                      : chunk,
                  )}
                </span>
              ),
              logo: () => <RareLogo />,
              img1: () => paraImage('img-1.png'),
              img2: () => paraImage('img-2.png'),
              img3: () => paraImage('img-3.png'),
            })}
          </p>
        </ContainerLG>
      </section>
    );
};
